import React, { useContext } from "react";
import { useNavigate } from "react-router-dom";
import {
  Button,
  Card,
  CardContent,
  Container,
  Typography,
  Box,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
} from "@mui/material";
import proces from "../images/proces.png";
import { UserDataContext } from "../components/UserDataContext";
import VN from "../components/VN";

import LoginIcon from "@mui/icons-material/Login";
import AppRegistrationIcon from "@mui/icons-material/AppRegistration";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import Face3OutlinedIcon from "@mui/icons-material/Face3Outlined";
import Person3OutlinedIcon from "@mui/icons-material/Person3Outlined";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import AccountTreeIcon from "@mui/icons-material/AccountTree";

export default function AppDescription() {
  const { userData } = useContext(UserDataContext);
  const navigate = useNavigate();

  return (
    <div
      style={{
        scrollBehavior: "smooth",
        backgroundColor: "#fafafa",
        padding: "0px 0",
      }}
    >
      <VN userData={userData} />

      <Container sx={{ py: 4 }}>
        <Box sx={{ mb: 5, textAlign: { xs: "center", sm: "left" } }}>
          <Typography
            variant="h4"
            component="h1"
            sx={{ fontWeight: 700, color: "#1a237e", mb: 1 }}
          >
            Opis działania aplikacji
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Tu jesteś w stanie zrozumieć jak działamy, by pozyskać jak najlepszą
            opiekę dla Twojego podopiecznego.
          </Typography>
        </Box>

        {/* 1. CEL DZIAŁANIA APLIKACJI */}
        <Card>
          <CardContent>
            <Button href="#cel-sekcja" startIcon={<InfoOutlinedIcon />}>
              Cel działania aplikacji
            </Button>

            <Box id="cel-sekcja" pt={2} mb={4} sx={{ scrollMarginTop: "90px" }}>
              <Typography
                variant="h5"
                component="h2"
                gutterBottom
                sx={{ fontWeight: "bold", color: "#1a237e" }}
              >
                Bezpieczeństwo i pewność wyboru
              </Typography>
              <Typography
                variant="body1"
                paragraph
                sx={{ color: "#555", lineHeight: "1.7" }}
              >
                Celem działania aplikacji jest połączenie osób szukających
                opieki ze zweryfikowaną osobą, posiadającą odpowiednie
                kwalifikacje i zweryfikowaną przez uprawnioną pielęgniarkę.
              </Typography>
              <Typography
                variant="body1"
                paragraph
                sx={{ color: "#555", lineHeight: "1.7", fontWeight: "500" }}
              >
                Każda opiekunka jest dokładnie sprawdzana. To, co nas wyróżnia,
                to rzetelna weryfikacja każdej osoby, zanim pojawi się w
                serwisie.
              </Typography>

              <Typography
                variant="subtitle1"
                sx={{ fontWeight: "bold", mt: 3, mb: 1, color: "#1a237e" }}
              >
                Sprawdzamy między innymi:
              </Typography>

              <List disablePadding>
                {[
                  "Tożsamość użytkownika",
                  "Doświadczenie w opiece nad seniorami",
                  "Referencje od poprzednich rodzin",
                  "Kwalifikacje oraz odbyte kursy",
                  "Prowadzimy indywidualną rozmowę weryfikacyjną",
                ].map((text, index) => (
                  <ListItem key={index} disablePadding sx={{ py: 0.5 }}>
                    <ListItemIcon sx={{ minWidth: "32px" }}>
                      <StarBorderIcon color="secondary" fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary={text} sx={{ color: "#444" }} />
                  </ListItem>
                ))}
              </List>

              <Box
                mt={3}
                p={2}
                sx={{
                  backgroundColor: "#e8f5e9",
                  borderRadius: "8px",
                  borderLeft: "4px solid #2e7d32",
                }}
              >
                <Typography
                  variant="body2"
                  sx={{ color: "#1b5e20", fontWeight: "500" }}
                >
                  Dzięki temu masz pewność, że korzystasz tylko ze sprawdzonych
                  opiekunek.
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>

        <Divider sx={{ my: 3 }} />

        {/* 2. DOSTĘP DO SYSTEMU */}
        <Card>
          <CardContent>
            <Button href="#dostep-sekcja" startIcon={<LoginIcon />}>
              Dostęp do systemu
            </Button>

            <Box
              id="dostep-sekcja"
              pt={2}
              mb={4}
              sx={{ scrollMarginTop: "90px" }}
            >
              <Typography
                variant="h5"
                component="h2"
                gutterBottom
                sx={{ fontWeight: "bold", color: "#1a237e" }}
              >
                Kto może korzystać z serwisu?
              </Typography>
              <Typography variant="body1" sx={{ color: "#555" }}>
                Do serwisu mają dostęp 3 dedykowane grupy użytkowników:
              </Typography>
              <List>
                {[
                  "Opiekunki",
                  "Osoby poszukujące opieki dla bliskich",
                  "Personel medyczny weryfikujący uprawnienia",
                ].map((text, index) => (
                  <ListItem key={index} disablePadding sx={{ py: 0.5 }}>
                    <ListItemIcon sx={{ minWidth: "32px" }}>
                      <StarBorderIcon color="secondary" fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary={text} />
                  </ListItem>
                ))}
              </List>
            </Box>
          </CardContent>
        </Card>

        <Divider sx={{ my: 3 }} />

        {/* 3. REJESTRACJA OPIEKUNKI */}
        <Card>
          <CardContent>
            <Button
              href="#rejestracja-opiekunki"
              startIcon={<AppRegistrationIcon />}
            >
              Rejestracja opiekunki
            </Button>

            <Box
              id="rejestracja-opiekunki"
              pt={2}
              mb={4}
              sx={{ scrollMarginTop: "90px" }}
            >
              <Typography
                variant="h5"
                component="h2"
                gutterBottom
                sx={{ fontWeight: "bold", color: "#1a237e" }}
              >
                Jak dołączyć jako Opiekunka?
              </Typography>
              <Typography variant="body1" paragraph sx={{ color: "#555" }}>
                Rejestracja opiekunki następuje poprzez wybranie przycisku{" "}
                <strong>"Dołącz jako opiekunka"</strong> i wypełnienie pól
                formularza.
              </Typography>

              <Typography
                variant="subtitle1"
                sx={{ fontWeight: "bold", mt: 2, mb: 1, color: "#1a237e" }}
              >
                Wymagane dane w formularzu:
              </Typography>

              <List dense>
                {[
                  {
                    title: "Email",
                    desc: "Weryfikowany w bazie danych",
                  },
                  {
                    title: "Nazwisko i imię",
                    desc: "Do kontaktów",
                  },
                  {
                    title: "Telefon",
                    desc: "Weryfikowany kodem SMS",
                  },
                  {
                    title: "Rok urodzenia & Obywatelstwo",
                    desc: "Podstawowe dane identyfikacyjne",
                  },
                  {
                    title: "Hasło",
                    desc: "Zabezpieczenie profilu",
                  },
                  {
                    title: "Województwo i Miasto",
                    desc: "Kluczowe do lokalizacji",
                  },
                ].map((item, index) => (
                  <ListItem key={index} alignItems="flex-start" sx={{ py: 1 }}>
                    <ListItemIcon sx={{ minWidth: "32px", mt: 0.5 }}>
                      <StarBorderIcon color="secondary" fontSize="small" />
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Typography variant="body1" fontWeight="bold">
                          {item.title}
                        </Typography>
                      }
                      secondary={
                        <Typography variant="body2" color="text.secondary">
                          {item.desc}
                        </Typography>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            </Box>
          </CardContent>
        </Card>

        <Divider sx={{ my: 3 }} />

        {/* 4. PROFIL OPIEKUNKI */}
        <Card>
          <CardContent>
            <Button href="#profil-opiekunki" startIcon={<Face3OutlinedIcon />}>
              Profil Opiekunki (Po zalogowaniu)
            </Button>

            <Box
              id="profil-opiekunki"
              pt={2}
              mb={4}
              sx={{ scrollMarginTop: "90px" }}
            >
              <Typography
                variant="h5"
                component="h2"
                gutterBottom
                sx={{ fontWeight: "bold", color: "#1a237e" }}
              >
                Kroki uzupełniania profilu opiekunki
              </Typography>

              <List dense>
                {[
                  {
                    title: "1. Dane personalne",
                    desc: "Weryfikacja e-mail kodem oraz opcja dodania zdjęcia profilowego.",
                  },
                  {
                    title: "2. Zakres obowiązków (Co mogę zrobić?)",
                    desc: "Zaznaczenie czynności (toaleta, podawanie leków, pielęgnacja itp.).",
                  },
                  {
                    title: "3. Obszar pracy (Gdzie mogę pracować?)",
                    desc: "Wybór miejscowości i województw, w których możesz podjąć zatrudnienie.",
                  },
                  {
                    title: "4. Kwalifikacje i uprawnienia",
                    desc: "Kursy opiekuna, wykształcenie medyczne, pierwsza pomoc.",
                  },
                  {
                    title: "5. Doświadczenie specjalistyczne",
                    desc: "Opieka nad chorymi na Alzheimer, demencję, Parkinson czy opieka paliatywna.",
                  },
                  {
                    title: "6. Języki obce",
                    desc: "Określenie stopnia znajomości języków.",
                  },
                  {
                    title: "7. Dodatkowe informacje i weryfikacja",
                    desc: "Wpisanie uwag własnych i kliknięcie 'Zakończ edycję i prześlij do weryfikacji' (profil zostanie zablokowany do czasu sprawdzenia przez koordynatora).",
                  },
                ].map((item, index) => (
                  <ListItem key={index} alignItems="flex-start" sx={{ py: 1 }}>
                    <ListItemIcon sx={{ minWidth: "32px", mt: 0.5 }}>
                      <StarBorderIcon color="secondary" fontSize="small" />
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Typography variant="body1" fontWeight="bold">
                          {item.title}
                        </Typography>
                      }
                      secondary={
                        <Typography variant="body2" color="text.secondary">
                          {item.desc}
                        </Typography>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            </Box>
          </CardContent>
        </Card>

        <Divider sx={{ my: 3 }} />

        {/* 5. REJESTRACJA SZUKAJĄCEGO */}
        <Card>
          <CardContent>
            <Button
              href="#rejestracja-szukajacego"
              startIcon={<Person3OutlinedIcon />}
            >
              Rejestracja osoby poszukującej
            </Button>

            <Box
              id="rejestracja-szukajacego"
              pt={2}
              mb={4}
              sx={{ scrollMarginTop: "90px" }}
            >
              <Typography
                variant="h5"
                component="h2"
                gutterBottom
                sx={{ fontWeight: "bold", color: "#1a237e" }}
              >
                Szukasz opiekunki dla bliskiego?
              </Typography>
              <Typography variant="body1" paragraph sx={{ color: "#555" }}>
                Wybierz przycisk{" "}
                <strong>"Dołącz jako szukający opiekunki"</strong>. Twoim
                loginem stanie się adres e-mail, który automatycznie
                zweryfikujemy bezpiecznym jednorazowym kodem.
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Podczas rejestracji poprosimy Cię o podanie: Imienia i nazwiska,
                numeru telefonu, bezpiecznego hasła oraz województwa i miasta, w
                którym docelowo ma być sprawowana opieka.
              </Typography>
            </Box>
          </CardContent>
        </Card>

        <Divider sx={{ my: 3 }} />

        {/* 6. PROCES WSPÓŁPRACY */}
        <Card>
          <CardContent>
            <Button href="#proces-wspolpracy" startIcon={<AccountTreeIcon />}>
              Proces współpracy
            </Button>

            <Box
              id="proces-wspolpracy"
              pt={2}
              mb={4}
              sx={{ scrollMarginTop: "90px" }}
            >
              <Typography
                variant="h5"
                component="h2"
                gutterBottom
                sx={{ fontWeight: "bold", color: "#1a237e" }}
              >
                Jak działa nasz system krok po kroku?
              </Typography>
              <Typography
                variant="body1"
                paragraph
                sx={{ color: "#555", mb: 3 }}
              >
                Poniższy schemat ilustruje przejrzystą ścieżkę weryfikacji i
                kontaktu. Dbamy o to, aby cały proces od rejestracji do
                znalezienia idealnej opieki przebiegał sprawnie i w bezpiecznych
                warunkach.
              </Typography>

              <Box sx={{ textAlign: "center" }}>
                <img
                  src={proces}
                  alt="Schemat procesu współpracy"
                  style={{
                    maxWidth: "100%",
                    height: "auto",
                    borderRadius: "8px",
                  }}
                />
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Container>
    </div>
  );
}
